import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import dayjs from 'dayjs';
import React, { Fragment, useContext, useMemo, useState } from 'react';
import { AccountType } from '../../../../__generated__/pge-types';
import { useTranslation } from '../../../../hooks/useTranslation';
import { featureFlagsContext } from '../../../../providers/FeatureFlagsProvider';
import ChevronRightIcon from '../../../../static/outageIconsV2/chevron_right.svg';
import colors from '../../../../themes/main-colors';
import { useIsMobile } from '../../../../util/style-utils';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';
import { AccountWithOutageInfo } from '../../types';
import CottageIcon from '../icons/CottageIcon';
import HomeWorkIcon from '../icons/HomeWorkIcon';
import { NestedOutageStatusAccordion } from '../nested-outage/NestedOutageStatusAccordion';
import { OutageStatusGraphic } from '../outage-status-graphic';
import { PSPSInfo } from '../psps-info';
import { ViewAllAccountsDialog } from '../view-all-accounts-dialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      cursor: 'pointer',
      borderTop: `1px solid ${colors.grey2}`,
      borderBottom: `1px solid ${colors.grey2}`,
      backgroundColor: colors.white,
    },
    collapsedContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(1.25, 0),
      textAlign: 'center',
      cursor: 'pointer',
    },
    iconCenter: {
      alignSelf: 'center',
    },
    bold: {
      fontWeight: 600,
    },
    backSection: {
      padding: theme.spacing(1.25, 2.5),
    },
    account: {
      fontSize: 18,
      fontWeight: 600,
    },
    location: {
      fontSize: 16,
    },
    accountOutages: {
      fontSize: 16,
      fontWeight: 600,
    },
    dot: {
      width: 12,
      height: 12,
      borderRadius: '50%',
      background: colors.roseRed,
      display: 'inline-block',
      marginRight: 10,
    },
    smallText: {
      fontSize: '0.75rem',
      fontWeight: 500,
    },
  }),
);

interface MultiAccountOutageDetailsProps {
  accountListWithOutageInfo: AccountWithOutageInfo[];
  refetchAccountsOutageInfo: () => void;
}

export const MultiAccountOutageDetails: React.FC<MultiAccountOutageDetailsProps> = ({
  accountListWithOutageInfo,
  refetchAccountsOutageInfo,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    panelOpen: expanded,
    selectedAccountForOutage,
    setSelectedAccountForOutage,
    setPspsDetails,
  } = useOutageLeftPanelState();
  const [openViewAllAccountsDialog, setOpenViewAllAccountsDialog] = useState(
    false,
  );
  const { page } = useOutageResponsivePageTracker();
  const isMobile = useIsMobile();
  const { isNestedOutageEnabled, loading: featureFlagLoading } = useContext(
    featureFlagsContext,
  );

  const accountsWithOutages = useMemo(
    () => accountListWithOutageInfo.filter(a => a.hasKnownOutage),
    [accountListWithOutageInfo],
  );

  const selectedOutagePremise =
    selectedAccountForOutage?.premiseInfos?.[0].servicePointDetails?.[0]
      ?.outageInfo;

  return expanded ? (
    <Fragment>
      <Box className={classes.container}>
        {selectedAccountForOutage ? (
          <>
            {(isMobile && page === ResponsivePageView.Home) || !isMobile ? (
              <Box
                display={'flex'}
                alignItems={'center'}
                className={classes.backSection}
                gridGap={10}
                borderBottom={`1px solid ${colors.grey2}`}
                onClick={async () => {
                  setSelectedAccountForOutage(undefined);
                  await refetchAccountsOutageInfo();
                }}
              >
                <ChevronLeft />
                <Typography variant="body2" className={classes.bold}>
                  {t('BACK')}
                </Typography>
              </Box>
            ) : (
              <></>
            )}
            <PSPSInfo />
            {!featureFlagLoading && isNestedOutageEnabled && (
              <OutageStatusGraphic
                step={0}
                hasKnownOutage={selectedAccountForOutage.hasKnownOutage}
              />
            )}
            {selectedAccountForOutage.hasKnownOutage ? (
              <NestedOutageStatusAccordion
                location={selectedAccountForOutage?.addressLocation}
                nestedOutageStatus={{
                  estimatedRestoration: selectedOutagePremise?.estimatedTimeOn
                    ? dayjs(
                        selectedOutagePremise?.estimatedTimeOn,
                        'MMMM D, YYYY h:mm a',
                      )
                        .format('MMM D, h:mm a')
                        .replace(/AM/gi, 'a.m.')
                        .replace(/PM/gi, 'p.m.')
                    : t('TO_BE_DETERMINED'),
                  restorationStatus: selectedOutagePremise?.eventStatus ?? '',
                  customersAffected:
                    selectedOutagePremise?.customersAffected ?? 0,
                  outageIdentified: selectedOutagePremise?.estimatedTimeOut
                    ? dayjs(
                        selectedOutagePremise?.estimatedTimeOut,
                        'MMMM D, YYYY h:mm a',
                      )
                        .format('MMM D, h:mm a')
                        .replace(/AM/gi, 'a.m.')
                        .replace(/PM/gi, 'p.m.')
                    : t('TO_BE_DETERMINED'),
                  causeOfOutage: selectedOutagePremise?.cause ?? '',
                }}
                triggerContent={
                  <Box display={'flex'} flexDirection={'column'} gridGap={20}>
                    <Box display={'flex'} alignItems={'center'} gridGap={10}>
                      {selectedAccountForOutage.accountType ===
                      AccountType.Com ? (
                        <HomeWorkIcon color={colors.roseRed} />
                      ) : (
                        <CottageIcon
                          width={32}
                          height={32}
                          color={colors.roseRed}
                        />
                      )}
                      <Box>
                        {selectedAccountForOutage?.isPrimaryAccount && (
                          <Typography variant="body2">
                            {t('PRIMARY')}
                          </Typography>
                        )}
                        <Typography variant="h6" className={classes.account}>
                          {selectedAccountForOutage?.accountNumber}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                }
              />
            ) : (
              <Box
                display={'flex'}
                flexDirection={'column'}
                gridGap={20}
                paddingY={3.75}
                paddingX={2.5}
              >
                <Box display={'flex'} alignItems={'center'} gridGap={10}>
                  {selectedAccountForOutage.accountType === AccountType.Com ? (
                    <HomeWorkIcon color={colors.pacificBlue} />
                  ) : (
                    <CottageIcon
                      width={32}
                      height={32}
                      color={colors.pacificBlue}
                    />
                  )}
                  <Box>
                    {selectedAccountForOutage?.isPrimaryAccount && (
                      <Typography variant="body2">{t('PRIMARY')}</Typography>
                    )}
                    <Typography variant="h6" className={classes.account}>
                      {selectedAccountForOutage?.accountNumber}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" className={classes.location}>
                  {selectedAccountForOutage?.addressLocation}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <>
            {accountsWithOutages.length > 0 && (
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
                padding={2.5}
                borderBottom={`1px solid ${colors.grey2}`}
              >
                <Typography variant="body1" className={classes.accountOutages}>
                  {t('ACCOUNT_OUTAGES')}
                </Typography>
                <Box display={'flex'} gridGap={10} alignItems={'center'}>
                  <Typography variant="body2" className={classes.bold}>
                    {accountsWithOutages.length}
                  </Typography>
                  <span className={classes.dot} aria-hidden="true"></span>
                </Box>
              </Box>
            )}

            <Box
              px={2.5}
              py={3.75}
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
              onClick={() => setOpenViewAllAccountsDialog(true)}
            >
              <Box display={'flex'} gridGap={10} alignItems={'center'}>
                <HomeWorkIcon
                  color={
                    accountsWithOutages.length > 0
                      ? colors.roseRed
                      : colors.pacificBlue
                  }
                />
                <Typography variant="body1" className={classes.bold}>
                  {t('VIEW_ALL_ACCOUNTS')}
                </Typography>
              </Box>
              <ChevronRightIcon fontSize={'large'} />
            </Box>
          </>
        )}
      </Box>

      <ViewAllAccountsDialog
        open={openViewAllAccountsDialog}
        setOpen={setOpenViewAllAccountsDialog}
        accountListWithOutageInfo={accountListWithOutageInfo}
        refetchAccountsOutageInfo={refetchAccountsOutageInfo}
      />
    </Fragment>
  ) : (
    <Box className={classes.collapsedContainer}>
      <div className={classes.iconCenter}>
        <HomeWorkIcon
          color={
            accountsWithOutages.length > 0 ? colors.roseRed : colors.pacificBlue
          }
        />
      </div>
      <Typography className={classes.smallText}>
        {t('ACCOUNT_LABEL')}
      </Typography>
    </Box>
  );
};
